import React, { HTMLAttributes, ReactNode, forwardRef } from 'react'
import { HiChevronRight } from 'react-icons/hi'
import { cn } from './cn'

export interface ListGroupProps extends HTMLAttributes<HTMLDivElement> {
  /** Card look (surface + hairline border). Off renders rows flush with hairlines only. */
  card?: boolean
}

/** A stack of rows with hairlines between them. */
export const ListGroup = forwardRef<HTMLDivElement, ListGroupProps>(
  ({ card = true, className, ...props }, ref) => (
    <div
      ref={ref}
      role="list"
      className={cn(
        'flex shrink-0 flex-col overflow-hidden [&>*+*]:border-t [&>*+*]:border-solid [&>*+*]:border-hairline',
        card && 'rounded-lg border border-solid border-hairline bg-surface',
        className
      )}
      {...props}
    />
  )
)
ListGroup.displayName = 'ListGroup'

export interface ListRowProps extends Omit<HTMLAttributes<HTMLElement>, 'onClick'> {
  leading?: ReactNode
  primary?: ReactNode
  secondary?: ReactNode
  trailing?: ReactNode
  /** Adds a chevron after the trailing slot. */
  chevron?: boolean
  onClick?: () => void
  disabled?: boolean
  selected?: boolean
  /** Standalone row (outside a ListGroup): rounded, no hairline. */
  inset?: boolean
  dense?: boolean
}

/** A list row: leading (icon / avatar / tile), primary, secondary, trailing. Focusable when clickable. */
export const ListRow = forwardRef<HTMLElement, ListRowProps>(
  (
    {
      leading,
      primary,
      secondary,
      trailing,
      chevron,
      onClick,
      disabled,
      selected,
      inset,
      dense,
      className,
      children,
      ...props
    },
    ref
  ) => {
    const interactive = !!onClick && !disabled
    const Comp = onClick ? 'button' : 'div'
    return (
      <Comp
        ref={ref as never}
        role={onClick ? undefined : 'listitem'}
        type={onClick ? 'button' : undefined}
        disabled={onClick ? disabled : undefined}
        onClick={interactive ? onClick : undefined}
        className={cn(
          'flex w-full items-center gap-3 border-0 bg-transparent text-left font-text text-fg',
          dense ? 'min-h-12 px-3 py-2' : 'min-h-[60px] px-4 py-2.5',
          '[--row-bg:var(--plug-surface)]',
          interactive &&
            'cursor-pointer transition-colors duration-150 hover:bg-surface-2 hover:[--row-bg:var(--plug-surface-2)]',
          'focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-focus',
          selected && 'bg-surface-2 [--row-bg:var(--plug-surface-2)]',
          inset && 'rounded-md',
          disabled && 'cursor-not-allowed opacity-40',
          className
        )}
        {...props}
      >
        {leading && <span className="flex shrink-0 items-center">{leading}</span>}
        {(primary !== undefined || secondary !== undefined) && (
          <span className="flex min-w-0 flex-1 flex-col gap-0.5">
            {primary !== undefined && (
              <span className="truncate text-[15px] leading-[1.3] font-semibold">{primary}</span>
            )}
            {secondary !== undefined && (
              <span className="truncate text-[13px] leading-[1.4] font-normal text-fg-2">
                {secondary}
              </span>
            )}
          </span>
        )}
        {children}
        {(trailing || chevron) && (
          <span className="ml-auto flex shrink-0 items-center gap-2 text-right text-fg-2">
            {trailing}
            {chevron && <HiChevronRight aria-hidden="true" className="size-5 text-fg-3" />}
          </span>
        )}
      </Comp>
    )
  }
)
ListRow.displayName = 'ListRow'

/** 40px circular tile for an icon or emoji in a row's leading slot. */
export const RowTile = ({ children, className }: { children: ReactNode; className?: string }) => (
  <span
    className={cn(
      'grid size-10 shrink-0 place-items-center overflow-hidden rounded-full bg-surface-2 text-[20px] text-accent [&_img]:size-full [&_img]:object-cover',
      className
    )}
  >
    {children}
  </span>
)
